/**
 * The Statistic menu: a search box over one sport's Stat Dictionary, and the
 * rows it matches.
 *
 * **A `<select>` cannot be searched and this list is too long to read.** The
 * live dictionary holds 392 football entries, 200 soccer, 141 hockey; scoping
 * each menu to the profile's sport (`client-feedback-round-2/03` and `05`)
 * fixed a correctness bug and left the length exactly as it was. What a native
 * menu offers instead of a search is the browser's own type-ahead, which
 * matches from the **start** of the label and resets after a second, so finding
 * *Yards After Catch* means knowing the dictionary files it under Y rather than
 * under Receiving — and a thirteen-report PFF source is a hundred of these
 * decisions in a row.
 *
 * Three screens pick a statistic out of the dictionary: both field-mapping
 * screens, through `StatisticCell`, and the eligibility rule on `SourceDetail`.
 * The control lives here rather than in `FieldMapping.tsx` because the third is
 * not a field-mapping screen, and the wording is the only thing that differs
 * between them — what "nothing chosen" means is a rule on `SourceDetail` (*rank
 * everyone*) and an absence on the other two.
 *
 * What matching means is `src/stat-search.ts`, which holds no React precisely
 * so that `tests/stat-search.test.ts` can run it.
 *
 * **The row under the cursor is described below the list.** Direction, the key,
 * the sport, the default weight, the Per-90 Derivation, the Distribution and
 * the aliases — every one of them already in the browser, and every one of them
 * discarded by a menu that printed a display name. Reading them meant the Stat
 * Dictionary, which is a different route, and both field-mapping screens hold
 * an unsaved review in client state: going to look discarded the configuration
 * (`field-mapping-usability/02`). The entry's `description` is **not** among
 * them, on the client's say-so; `stat-search.ts` has the measurement behind
 * that and `StatDetailPanel` has the rest of the argument.
 *
 * **Nothing about the search decides what is stored.** It filters the rows
 * offered and nothing else: no row is auto-selected, an exact match is not
 * committed on blur, and a term that matches nothing leaves the control holding
 * exactly what it held. That is the same rule the menus it replaces kept by
 * being `<select>`s, and it matters more here than there, because a `stat_key`
 * picked by accident is a published column under the wrong name with the wrong
 * Direction — or, on the eligibility rule, a threshold against the wrong
 * statistic — and nothing raising (ADR-0017).
 *
 * **Each row names the sport it came from**, in a badge at its right. The menu
 * is scoped to one sport, so on most rows that repeats what the whole list
 * already is; it is on the list anyway because the rows that disagree are the
 * dangerous ones — the out-of-sport row a stored `stat_key` adds, and a
 * dictionary entry whose **Sport** moved out from under a bound Binding — and
 * because sport is half of what a statistic *is*: `penalties` is flags and
 * lower-is-better in football, penalty kicks and higher-is-better in soccer
 * (ADR-0017). It is a label and not a search key; `stat-search.ts` says why.
 *
 * **The value is shown by the box, not by the list.** This is the one thing the
 * change makes structurally safer rather than merely faster: a `<select>` whose
 * value matches none of its options renders as the first option or as blank —
 * the defect behind `client-feedback-round-2/01`, the one `03` and `05` had to
 * avoid reintroducing, and the one `add-source-usability/01`'s review found
 * again in the Source menu. A text box displays the label it was given whatever
 * the list below it currently holds, so searching cannot make a control
 * misreport what is stored.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";

import { clearRowNote, matchStats, type StatDetail, type StatOption } from "../stat-search";
// The height budget, in its own module so `tests/stat-menu-layout.test.ts` can
// assert it: four numbers deciding whether either half of this menu is legible
// should fail a test rather than a screenshot.
import { panelHeight } from "../stat-menu-layout";
// The placement, the keys and the ARIA wiring this control worked out are
// shared with the League menu on Add Source; `Combobox.tsx` holds them.
import { usePlacement } from "./Combobox";
import { cx } from "./ui";
import { CheckIcon, DirectionIcon } from "./icons";

/**
 * The row that puts the control back to holding no statistic.
 *
 * Its wording is the caller's, because holding nothing is not the same fact on
 * every screen. On a field-mapping row it is an absence — the field is not
 * bound to anything yet. On the eligibility rule it is a **decision**, and the
 * decision is *rank everyone*: a screen that called it "clear" there would be
 * describing the control instead of the profile.
 */
function clearRow(label: string): StatOption {
  // No statistic behind it, so nothing for the detail panel to describe. The
  // panel says so rather than rendering an empty frame.
  return { key: "", label, sport: "", offDictionary: false, search: [], detail: null };
}

/**
 * The sport a row's statistic was read from, as a badge at the right of the row.
 *
 * Smaller than `ui.tsx`'s `Badge`, which is `caption` at 13px — the same size as
 * the label it would sit beside — in a list whose rows are 29px tall and whose
 * panel is 280px at its narrowest. `micro` is the token for exactly this: the
 * smallest legible metadata inside a dense row.
 *
 * It never takes width from the label: the label truncates and this does not,
 * so a long statistic in a narrow cell loses its tail rather than its sport.
 * Rendered nowhere else — the shut box is an `<input>` and can hold only text,
 * which is also why the statistic's own name keeps the whole of it there.
 *
 * `aria-hidden`, because the row already says the sport to a screen reader: on
 * the out-of-sport row it is in the label, and on every other row it is the
 * sport the whole menu is scoped to, announced once by the listbox rather than
 * 392 times.
 */
function SportBadge({ sport }: { sport: string }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "shrink-0 whitespace-nowrap rounded-pill border border-hairline",
        "bg-surface-soft px-xs py-[1px] text-micro text-muted-soft",
      )}
    >
      {sport}
    </span>
  );
}

/**
 * What the dictionary knows about the row under the cursor, under the rows.
 *
 * **The screen it saves a trip to is the point.** Direction, the description
 * and the rest live on the Stat Dictionary, which is a different route — and
 * both field-mapping screens hold an unsaved review in client state, so going
 * to read one discards every decision made so far. On a thirteen-report PFF
 * source that is a hundred-odd of them, which is why the client asked for this
 * on the screen rather than as a better link (`field-mapping-usability/02`).
 *
 * **It previews in both directions from one control.** Arrowing or hovering
 * through the matches reads each candidate out before anything is stored — the
 * `interceptions_thrown` / `interceptions_caught` pair, two rows a search for
 * `int` returns side by side with opposite Directions, settled without leaving
 * the row. And a menu *opened* over a configured cell lands on the row already
 * chosen (`show()`), so the statistic this field is bound to is the first thing
 * the panel describes; Escape closes it having stored nothing.
 *
 * **Under the list rather than in the cell, because the cell has no room and
 * this is settled history.** The Statistic column has twice lost a fight for
 * width and both are recorded in `FieldMapping.tsx`: `+ New` was cut to `+`
 * because the words cost 45px and truncated *Field Goals Made* to `F`, and
 * `+` is on the per-Feed lines too, now that a field read apart has no row of
 * its own to invent a statistic from, so that cell holds two controls on every
 * line that offers any. An ⓘ button here would be the third. A panel
 * drawn inside a floating menu costs the table nothing.
 *
 * What it costs instead is menu height, and that is paid for rather than taken
 * from the list: `usePlacement` is asked for 440 instead of 320, and
 * `stat-menu-layout.ts` divides the result so the rows keep the space
 * `field-mapping-usability/01` won for them.
 *
 * `aria-live` is deliberately absent, and the panel is reached instead through
 * `aria-describedby` on the **active option** — the APG's wiring for a listbox
 * whose rows carry more than a name. A live region would announce the panel
 * over the top of the row name the listbox is already reading on every arrow
 * key; a description on the row is read as part of that row, in the order and
 * at the verbosity the reader has chosen. On the *box* it would be read once,
 * when focus arrives, and never again as the rows change under it — which is
 * every row after the first, and is what the first cut of this did.
 */
function StatDetailPanel({
  detail,
  id,
  maxHeight,
  /** What to say where there is no statistic behind the row, which is the
   *  *clear* row and nothing else. Passed in rather than written here, so the
   *  panel holds no opinion about what the rows mean. */
  emptyNote,
}: {
  detail: StatDetail | null;
  id: string;
  /** What `panelHeight` allowed it, given the menu it is drawn in. */
  maxHeight: number;
  emptyNote: string;
}) {
  return (
    <div
      id={id}
      className={cx(
        // Sized by `panelHeight` and otherwise rigid, so the rows above get
        // exactly what is left and nothing races for it.
        "shrink-0 overflow-y-auto overscroll-contain",
        "border-t border-hairline bg-surface-soft px-sm py-xs",
      )}
      style={{ maxHeight }}
    >
      {detail === null ? (
        <span className="text-micro text-muted-soft">{emptyNote}</span>
      ) : (
        <div className="flex flex-col gap-xxs">
          {/* The fact the panel exists for, first and in words. A label saying
              "(lower is better)" and nothing saying the opposite leaves 334 of
              football's 403 entries declaring an absence, which is what sent
              the client to the dictionary to check.

              Absent where the menu's choice is not ranked — the eligibility
              rule compares a minimum and never reads a Direction — which
              `statDetail` decides and this only renders. */}
          {detail.direction !== null && (
            <div className="flex items-center gap-xxs text-caption text-ink">
              <DirectionIcon
                size={14}
                higherIsBetter={detail.higherIsBetter}
                className="shrink-0"
              />
              <span>{detail.direction}</span>
            </div>
          )}

          {detail.directionNote && (
            <p className="m-0 text-micro text-warning-ink">{detail.directionNote}</p>
          )}

          {/* Wrapping rather than truncating: these are short and there is
              nothing here worth hiding the end of. */}
          <dl className="m-0 flex flex-wrap gap-x-sm gap-y-xxs text-micro text-muted">
            {detail.facts.map((fact) => (
              <div key={fact.label} className="flex min-w-0 items-baseline gap-xxs">
                <dt className="shrink-0 text-muted-soft">{fact.label}</dt>
                <dd className="m-0 break-all text-ink">{fact.value}</dd>
              </div>
            ))}
          </dl>

          {/*
            Its own line rather than one more cell in the `<dl>` above: those
            are short values on one row and break mid-word, which is right for a
            key and wrong for a list of labels. And this is the fact a reviewer
            is choosing *between* statistics on, so it reads as a sentence.

            Named by what it does to the ranking rather than by the control's
            caption. "Positions Groups Excluded" is the client's own name for
            the dropdown and stays on it, but the glossary's account of the
            concept is the groups a statistic *does not rank* — which is what
            somebody reading this menu needs to know.
          */}
          {detail.excluded.length > 0 && (
            <p className="m-0 text-micro text-muted">
              Does not rank{" "}
              {detail.excluded
                .map((row) =>
                  row.labels.length > 0
                    ? `${row.group} (${row.labels.join(", ")})`
                    : row.group,
                )
                .join(", ")}
            </p>
          )}

          {detail.aliases.length > 0 && (
            <p className="m-0 text-micro text-muted-soft">
              {/* The one thing this module searches and never showed. A row
                  found by typing `QBR` now says that `QBR` is why. */}
              Also found by {detail.aliases.join(", ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function StatisticMenu({
  value,
  options,
  label,
  placeholder = "Choose a statistic…",
  clearLabel = "Clear the statistic",
  compact = true,
  onChange,
}: {
  value: string | null;
  options: readonly StatOption[];
  /** What a screen reader calls this box. In a table the cell is one of five or
   *  six with no visible label of its own, so the caller names it after the
   *  field — as the Weight box beside it already does. Under a `Field` it is
   *  the label already on screen, said twice rather than differently. */
  label?: string;
  /** What the box says when nothing is chosen. */
  placeholder?: string;
  /** What the row that chooses nothing says. */
  clearLabel?: string;
  /** In-table size, which is every caller but the eligibility rule. */
  compact?: boolean;
  onChange: (statKey: string | null) => void;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();

  const [open, setOpen] = useState(false);
  // Emptied whenever the menu closes, so the box is never displaying a search
  // term over a cell that is not searching.
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  // Wider than the shared floor of 280: the sport badge and the check mark sit
  // on the same line as the label, and at 280 "Final Third Pass Accuracy %" —
  // a real Wyscout row — truncated to "Final Third Pass Accurac…". The menu is
  // the one place the whole name is readable, since the shut box is an input
  // the cell's own width crops.
  // 440 rather than the shared 320, because the detail panel sits under the
  // rows and must not be paid for out of them: at 320 a football menu showed
  // six of 403 statistics. A ceiling only — a cell near the foot of the window
  // still gets whatever space is actually there.
  const placement = usePlacement(anchor, open, 340, 440);
  const matches = useMemo(() => matchStats(options, query), [options, query]);
  const chosen = options.find((option) => option.key === value) ?? null;
  const searching = query.trim() !== "";

  // Offered only when there is something to clear, and only while the list is
  // unsearched: a row that matches no term has no business appearing among the
  // rows that do.
  const rows = useMemo(
    () => (value !== null && !searching ? [clearRow(clearLabel), ...matches] : matches),
    [clearLabel, matches, searching, value],
  );

  // The row the detail panel describes, which is the row the keyboard and the
  // mouse already agree on — `active` is set by both. Undefined where a search
  // matched nothing, and then there is no panel rather than an empty one.
  const activeRow = rows[active];

  // A new search is read from the top. Not folded into the `onChange` that sets
  // the query, because `rows` is what the index points into and it is derived.
  useEffect(() => setActive(0), [query]);

  // The active row scrolled to, rather than merely highlighted. Arrowing into a
  // row nobody can see is how a keyboard user loses a list of 392.
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, active, rows]);

  function show() {
    if (open) return;
    setQuery("");
    // Opening onto the row already chosen, so a menu opened by mistake is
    // closed by Enter without changing anything.
    const index = options.findIndex((option) => option.key === value);
    setActive(index < 0 ? 0 : index + (value !== null ? 1 : 0));
    setOpen(true);
  }

  function hide() {
    setOpen(false);
    setQuery("");
  }

  function choose(row: StatOption) {
    onChange(row.key === "" ? null : row.key);
    hide();
    box.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        show();
        return;
      }
      if (rows.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => (current + step + rows.length) % rows.length);
      return;
    }
    if (event.key === "Home" || event.key === "End") {
      if (!open || rows.length === 0) return;
      event.preventDefault();
      setActive(event.key === "Home" ? 0 : rows.length - 1);
      return;
    }
    if (event.key === "Enter") {
      // Only when the menu is open, and then it is this control's key rather
      // than the form's: `NewStatisticForm` is a real form so that Enter adds a
      // statistic, and a row chosen here must not also submit it.
      if (!open) return;
      event.preventDefault();
      const row = rows[active];
      if (row) choose(row);
      return;
    }
    if (event.key === "Escape") {
      if (!open) return;
      event.preventDefault();
      // So a menu open inside a dialog closes the menu rather than the dialog.
      event.stopPropagation();
      hide();
      return;
    }
    // Tab leaves the control, and leaving it is not choosing anything: the cell
    // keeps whatever it already held.
    if (event.key === "Tab") hide();
  }

  return (
    // Closed by clicking anywhere outside, which for a control whose only
    // focusable part is the box is exactly "the box lost focus". The rows
    // refuse focus on mousedown, so clicking one does not blur first and close
    // the list out from under the click.
    <div
      ref={anchor}
      /* `min-w-0` lets a flex child shrink past its content, which in an
         auto-layout table means the column gives way to every other one: at a
         1280px window the Fields table squeezed this box to **46px** — a
         chevron and nothing else — on all 216 scored rows. `TableScroll`
         already scrolls this table sideways, so a floor here buys a readable
         control at the price of a scrollbar the table had anyway, which is the
         better half of that trade. 12rem fits "Assists (Coverage)"; the long
         "(lower is better)" names still need a wide window, and get one.

         **And a ceiling, because `flex-1` with no cap is greedy.** This is the
         only cell in the row that can grow, so an auto-layout table handed the
         Statistic column every spare pixel — 613px of a 1590px table, while
         Sample sat on 73 and a citizenship read `Arge…`. 24rem clears the
         longest name the dictionary holds ("Average Starting Field Position
         (lower is better)") and hands the rest back to the columns that had
         none. */
      className="min-w-[12rem] max-w-[24rem] flex-1"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) hide();
      }}
    >
      <input
        ref={box}
        type="text"
        role="combobox"
        className={cx("input combobox w-full", compact && "input-sm")}
        aria-label={label}
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={open && activeRow ? `${id}-row-${active}` : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder={open ? "Search statistics…" : placeholder}
        // The chosen label whenever the menu is shut, whatever the list holds —
        // see the header. While it is open the box is the search term.
        value={open ? query : (chosen?.label ?? "")}
        // So the first keystroke after tabbing in replaces the label rather than
        // being appended to it.
        onFocus={() => box.current?.select()}
        onMouseDown={show}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />

      {open && placement && (
        <div
          className={cx(
            "fixed z-50 flex flex-col overflow-hidden rounded-md",
            "border border-hairline bg-canvas shadow-overlay",
          )}
          style={{
            left: placement.left,
            width: placement.width,
            top: placement.top,
            bottom: placement.bottom,
            maxHeight: placement.maxHeight,
          }}
        >
          {/* How much of the dictionary is on screen, which is the question the
              length of this list raises and a scrollbar answers badly. Announced
              as well as shown: a term typed into a box whose list is elsewhere
              tells a screen reader nothing by itself. */}
          <div
            role="status"
            aria-live="polite"
            className="shrink-0 border-b border-hairline px-sm py-xxs text-caption text-muted-soft"
          >
            {searching
              ? `${matches.length} of ${options.length} match “${query.trim()}”`
              : `${options.length} ${options.length === 1 ? "statistic" : "statistics"} · type to search`}
          </div>

          <ul
            ref={list}
            id={`${id}-list`}
            role="listbox"
            aria-label={label}
            className="m-0 min-h-0 flex-1 list-none overflow-y-auto overscroll-contain p-0"
          >
            {rows.map((row, index) => (
              <li
                key={row.key === "" ? "clear-row" : `stat-${row.key}`}
                id={`${id}-row-${index}`}
                role="option"
                aria-selected={row.key !== "" && row.key === value}
                // The panel describes whichever row is active, so the
                // description belongs to *that* row. On the box instead, it is
                // announced once when focus arrives and never again as the
                // rows change under it — which is every row after the first.
                //
                // Only the active one carries it: an IDREF from all 403 to one
                // panel would be 403 rows claiming the same description, and
                // 402 of them would be claiming the wrong one.
                aria-describedby={index === active ? `${id}-detail` : undefined}
                data-active={index === active}
                title={row.sport === "" ? row.label : `${row.label} · ${row.sport}`}
                className={cx(
                  "flex cursor-pointer items-center justify-between gap-xs px-sm py-xs text-caption",
                  index === active && "bg-surface-soft",
                  row.key === "" || row.offDictionary ? "text-muted" : "text-ink",
                )}
                // Keeps focus in the box, so the click that follows lands on a
                // menu that is still open.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(row)}
              >
                <span className="truncate">{row.label}</span>
                {row.sport !== "" && <SportBadge sport={row.sport} />}
                {row.key !== "" && row.key === value && (
                  <CheckIcon size={14} className="shrink-0" />
                )}
              </li>
            ))}
          </ul>

          {activeRow && (
            <StatDetailPanel
              id={`${id}-detail`}
              detail={activeRow.detail}
              maxHeight={panelHeight(placement.maxHeight)}
              emptyNote={clearRowNote(activeRow.label)}
            />
          )}

          {/* The dead end ADR-0029 exists to close, named rather than left as an
              empty box: nothing matches, and the control that fixes that is the
              button next to the one being typed into. */}
          {rows.length === 0 && (
            <div className="shrink-0 px-sm py-sm text-caption text-muted">
              No statistic matches “{query.trim()}”. <strong>+ New</strong> adds one to the{" "}
              {options.length === 0 ? "empty " : ""}dictionary.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
